import { useState } from 'react';
import { X, Phone, RefreshCw, ChevronDown } from 'lucide-react';
import { countryCodes, defaultCountry } from '../../data/countryCodes';

const PhoneVerificationModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Enter phone, 2: Verify code
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  if (!isOpen) return null;

  const handleSendCode = async (e) => {
    e.preventDefault();

    if (!phoneNumber) {
      setError('Please enter your phone number');
      return;
    }

    // Combine country code with phone number
    const fullPhoneNumber = `${selectedCountry.dialCode}${phoneNumber}`;

    // Basic phone validation (should be 10-15 digits total)
    const digitsOnly = fullPhoneNumber.replace(/\D/g, '');
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Using apiPost with automatic token refresh
      const { apiPost } = await import('../../utils/api');
      const response = await apiPost('/two_factor/phone/send_verification', { phone: fullPhoneNumber });

      const data = await response.json();

      if (response.ok) {
        setStep(2);
      } else {
        setError(data.error || 'Failed to send verification code');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Using apiPost with automatic token refresh
      const { apiPost } = await import('../../utils/api');
      const response = await apiPost('/two_factor/phone/verify', { code: verificationCode });

      const data = await response.json();

      if (response.ok) {
        onSuccess();
        handleClose();
      } else {
        setError(data.error || 'Invalid verification code');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');

    try {
      const fullPhoneNumber = `${selectedCountry.dialCode}${phoneNumber}`;

      // Using apiPost with automatic token refresh
      const { apiPost } = await import('../../utils/api');
      const response = await apiPost('/two_factor/phone/send_verification', { phone: fullPhoneNumber });

      const data = await response.json();

      if (response.ok) {
        // Show success message
        setError('Code sent successfully');
        setTimeout(() => setError(''), 3000);
      } else {
        setError(data.error || 'Failed to resend code');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedCountry(defaultCountry);
    setPhoneNumber('');
    setVerificationCode('');
    setError('');
    setShowCountryDropdown(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-dm-serif font-semibold text-gray-900 dark:text-white">
            {step === 1 ? 'Verify Phone Number' : 'Enter Verification Code'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {step === 1 ? (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-rasa">
                Enter your phone number to receive a verification code via SMS
              </p>

              <form onSubmit={handleSendCode} className="space-y-4">
                <div>
                  <label className="block text-sm font-rasa font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <div className="flex space-x-2">
                    {/* Country Selector */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                        className="flex items-center space-x-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-2 focus:ring-blue-500"
                      >
                        <span className="text-xl">{selectedCountry.flag}</span>
                        <span className="font-medium">{selectedCountry.dialCode}</span>
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </button>

                      {/* Country Dropdown */}
                      {showCountryDropdown && (
                        <div className="absolute z-10 mt-1 w-72 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {countryCodes.map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              onClick={() => {
                                setSelectedCountry(country);
                                setShowCountryDropdown(false);
                              }}
                              className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-left font-rasa"
                            >
                              <span className="text-xl">{country.flag}</span>
                              <span className="flex-1 text-sm text-gray-900 dark:text-white">{country.name}</span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">{country.dialCode}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Phone Input */}
                    <div className="flex-1 relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          setPhoneNumber(value);
                          setError('');
                        }}
                        placeholder="1234567890"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-rasa"
                      />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 font-rasa">
                    Enter your phone number without the country code
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400 p-3 rounded-lg text-sm font-rasa">
                    {error}
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-rasa font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-rasa font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending...' : 'Send Code'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-rasa">
                Enter the 6-digit code sent to {selectedCountry.dialCode} {phoneNumber}
              </p>

              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div>
                  <label className="block text-sm font-rasa font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setVerificationCode(value);
                      setError('');
                    }}
                    placeholder="000000"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-xl tracking-widest"
                    autoFocus
                  />
                </div>

                {error && (
                  <div className={`p-3 rounded-lg text-sm font-rasa ${
                    error.includes('sent')
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400'
                  }`}>
                    {error}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="flex items-center justify-center space-x-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50 w-full font-rasa"
                >
                  <RefreshCw className={`h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
                  <span>{resending ? 'Sending...' : 'Resend Code'}</span>
                </button>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-rasa font-medium"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading || verificationCode.length !== 6}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-rasa font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Verifying...' : 'Verify'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationModal;
